<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesAICopyAlert from './SalesAICopyAlert.vue';
import SalesPartyIdentityFields from './SalesPartyIdentityFields.vue';
import SalesPartyInternalProfileAlert from './SalesPartyInternalProfileAlert.vue';
import SalesPartyMaritalSection from './SalesPartyMaritalSection.vue';
import SalesPartyRepresentativeRoles from './SalesPartyRepresentativeRoles.vue';
const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  internalProfile: { type: Boolean, default: false },
  internalProfileAlert: { type: Boolean, default: false },
  lookupError: { type: String, default: '' },
  lookupLoading: { type: Boolean, default: false },
  partyRole: { type: String, default: '' },
  representative: { type: Boolean, default: false },
  representativeRolesLocked: { type: Boolean, default: false },
  deliveryCorrection: { type: Boolean, default: false },
  showSpouseCpf: { type: Boolean, default: true },
  spouseLookupError: { type: String, default: '' },
  spouseLookupLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const internalProfileAlertDismissed = ref(false);
const identity = computed(() => props.profile.identity || {});
const roleLabel = computed(
  () =>
    ({
      vendedor: t('SALES_PARTIES.PERSONAL.SELLER_ROLE'),
      comprador: t('SALES_PARTIES.PERSONAL.BUYER_ROLE'),
    })[props.partyRole]
);
const roleAction = computed(
  () =>
    ({
      vendedor: t('SALES_PARTIES.PERSONAL.SELLING_ACTION'),
      comprador: t('SALES_PARTIES.PERSONAL.BUYING_ACTION'),
    })[props.partyRole]
);
const internalProfileAction = computed(
  () =>
    ({
      vendedor: t('SALES_PARTIES.PERSONAL.INTERNAL_PROFILE_SELL_ACTION'),
      comprador: t('SALES_PARTIES.PERSONAL.INTERNAL_PROFILE_BUY_ACTION'),
    })[props.partyRole] || ''
);
const hasFormContext = computed(() => Boolean(roleLabel.value));
const title = computed(() => {
  if (props.deliveryCorrection && roleLabel.value) {
    return t('SALES_PARTIES.PERSONAL.FORM_TITLE', {
      role: roleLabel.value,
    });
  }
  if (roleAction.value) {
    return t('SALES_PARTIES.PERSONAL.FORM_TITLE_NORMAL', {
      action: roleAction.value,
    });
  }
  return t('SALES_PARTIES.PERSONAL.TITLE');
});
const update = (field, value) => emit('update', 'identity', field, value);

watch(
  () => props.internalProfileAlert,
  show => {
    if (!show) internalProfileAlertDismissed.value = false;
  }
);
</script>

<template>
  <LaisCard :title="title">
    <template #header>
      <h2
        class="text-lais-heading-sm font-semibold leading-tight text-lais-text font-redHatDisplay"
      >
        {{ title }}
      </h2>
    </template>
    <div class="grid grid-cols-1 gap-5">
      <p
        v-if="hasFormContext"
        class="text-sm text-lais-text-muted"
        data-test="sales-party-personal-description"
      >
        {{ t('SALES_PARTIES.PERSONAL.FORM_DESCRIPTION') }}
        <span class="text-xl font-medium">
          {{ t('SALES_PARTIES.PERSONAL.FORM_WARNING') }}
        </span>
      </p>
      <p
        v-else
        class="text-sm text-lais-text-muted"
        data-test="sales-party-personal-description"
      >
        {{ t('SALES_PARTIES.PERSONAL.DESCRIPTION') }}
      </p>
      <SalesAICopyAlert :message="t('SALES_PARTIES.COMMON.AI_COPY_WARNING')" />
      <SalesPartyRepresentativeRoles
        v-if="representative"
        :error="errors.representative_role"
        :identity="identity"
        :locked="representativeRolesLocked"
        @update="update"
      />
      <SalesPartyIdentityFields
        :errors="errors"
        :has-form-context="hasFormContext"
        :identity="identity"
        :internal-profile="internalProfile"
        :lookup-error="lookupError"
        :lookup-loading="lookupLoading"
        :role-label="roleLabel"
        @update="update"
      />
      <SalesPartyMaritalSection
        :errors="errors"
        :identity="identity"
        :reference-labels="hasFormContext"
        :show-spouse-cpf="showSpouseCpf"
        :spouse-lookup-error="spouseLookupError"
        :spouse-lookup-loading="spouseLookupLoading"
        @update="update"
      />
      <SalesPartyInternalProfileAlert
        :action="internalProfileAction"
        :show="internalProfileAlert && !internalProfileAlertDismissed"
        @close="internalProfileAlertDismissed = true"
      />
    </div>
  </LaisCard>
</template>
